import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../ThemeContext';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { darkMode } = useTheme();

  // Select logo based on theme
  const logoSrc = darkMode
    ? '/assets/images/logo_dk_theme.svg'
    : '/assets/images/logo_lt_theme.svg';

  // Track scroll position for header blur effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/episodes', label: 'Episodes' },
    { path: '/contact', label: 'Contact' },
  ];

  // Animation variants
  const menuVariants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const menuItemVariants = {
    closed: { opacity: 0, y: 20 },
    open: { opacity: 1, y: 0 },
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'glass border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link
              to="/"
              className="relative z-10 flex-shrink-0 group"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="h-10 sm:h-12"
              >
                <img
                  src={logoSrc}
                  alt="News from the Pew"
                  className="h-full w-auto transition-opacity duration-300 group-hover:opacity-90"
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <ul className="flex items-center gap-8">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className={`nav-link font-medium text-sm tracking-wide uppercase ${
                        isActive(link.path)
                          ? 'text-accent active'
                          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href="https://www.jdavidwyatt.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-link font-medium text-sm tracking-wide uppercase text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    Music & Books
                  </a>
                </li>
              </ul>
              <div className="pl-4 border-l border-[var(--bg-tertiary)]">
                <ThemeToggle />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="md:hidden relative z-10 p-2 text-[var(--text-primary)] hover:text-accent transition-colors"
              onClick={toggleMenu}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={28} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={28} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[var(--bg-primary)]/95 backdrop-blur-xl"
              onClick={toggleMenu}
            />

            {/* Menu Content */}
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="relative h-full flex flex-col items-center justify-center"
            >
              <ul className="flex flex-col items-center gap-8">
                {navLinks.map((link, index) => (
                  <motion.li
                    key={link.path}
                    variants={menuItemVariants}
                    custom={index}
                  >
                    <Link
                      to={link.path}
                      onClick={toggleMenu}
                      className={`text-3xl font-display font-medium tracking-wide transition-colors ${
                        isActive(link.path)
                          ? 'text-accent'
                          : 'text-[var(--text-primary)] hover:text-accent'
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
                <motion.li variants={menuItemVariants}>
                  <a
                    href="https://www.jdavidwyatt.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={toggleMenu}
                    className="text-3xl font-display font-medium tracking-wide transition-colors text-[var(--text-primary)] hover:text-accent"
                  >
                    Music & Books
                  </a>
                </motion.li>
              </ul>

              {/* Mobile Theme Toggle */}
              <motion.div
                variants={menuItemVariants}
                className="mt-12 pt-8 border-t border-[var(--bg-tertiary)]"
              >
                <ThemeToggle />
              </motion.div>

              {/* Decorative gradient line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="absolute bottom-20 left-1/2 -translate-x-1/2 w-32 h-0.5 bg-gradient-subtle rounded-full"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer for fixed nav */}
      <div className="h-20" />
    </>
  );
};

export default Navigation;
